import { useState } from 'react'

import { useFacilitySearchOptions } from '@/hooks/useFacilitySearchOptions'
import { useToasts } from '@/hooks/useToasts'
import { SubmitHandler } from 'react-hook-form'

import {
  useAssociateMultipleIncidentsToSingleCaseMutation,
  useCreateCaseMutation,
} from '@/graphql/generated/hooks'

import { CreateCaseFormInputsIProps } from '../types/types'

interface UseCreateCaseIProps {
  incidentId?: string
  onClose: () => void
}

export const useCreateCase = ({ incidentId, onClose }: UseCreateCaseIProps) => {
  const [createCase] = useCreateCaseMutation()
  const [associateIncidentsToCase] =
    useAssociateMultipleIncidentsToSingleCaseMutation()
  const [isLoading, setIsLoading] = useState(false)
  const { showSuccess, showError } = useToasts()

  const {
    facilityOptions,
    isFacilitiesLoading,
    isFacilitiesError,
    setFacilityName,
  } = useFacilitySearchOptions()

  const onCreateCase: SubmitHandler<CreateCaseFormInputsIProps> = async (
    values
  ) => {
    const facilityIds = values.facilities?.map((f) => f.value) || []
    setIsLoading(true)
    try {
      const { data } = await createCase({
        variables: {
          input: {
            description: values?.description,
            facilityIds,
            name: values?.name,
          },
        },
        refetchQueries: ['Cases'],
      })
      const caseReturnedName = data.createCase?.case?.name
      const caseId = data.createCase?.case?.id
      // If incidentId, associate it with the case
      if (incidentId) {
        await associateIncidentsToCase({
          variables: { input: { caseId, incidentIds: [incidentId] } },
        })
      }

      showSuccess(`Successfully created ${caseReturnedName}`)
      setIsLoading(false)
      onClose()
    } catch (error) {
      showError(error.message)
      setIsLoading(false)
    }
  }

  return {
    facilityOptions,
    isFacilitiesLoading,
    isFacilitiesError,
    isLoading,
    onCreateCase,
    setFacilityName,
  }
}
